import { useNostr } from "@nostrify/react";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { NSecSigner } from "@nostrify/nostrify";
import { generateSecretKey } from "nostr-tools";

import {
  ADMIN_PUBKEY,
  ORDER_KIND,
  ORDER_RELAYS,
  type OrderPayload,
} from "@/lib/cashcraft";

import type { NostrEvent } from "@nostrify/nostrify";

/**
 * Submits a website build order with zero friction — no login required.
 *
 * A throwaway (ephemeral) Nostr keypair is generated on the fly to sign the
 * order. The order payload is encrypted with NIP-44 to the operator (admin)
 * pubkey, so only the operator can read it, then published as a kind 4011
 * event that p-tags the operator.
 */
export function useSubmitOrder(): UseMutationResult<
  NostrEvent,
  Error,
  OrderPayload
> {
  const { nostr } = useNostr();

  return useMutation({
    mutationFn: async (payload: OrderPayload) => {
      // Ephemeral, single-use identity for this order.
      const signer = new NSecSigner(generateSecretKey());

      const plaintext = JSON.stringify(payload);
      const ciphertext = await signer.nip44.encrypt(ADMIN_PUBKEY, plaintext);

      const tags: string[][] = [
        ["p", ADMIN_PUBKEY],
        ["alt", "CashCraft website build order"],
      ];

      if (location.protocol === "https:") {
        tags.push(["client", location.hostname]);
      }

      const event = await signer.signEvent({
        kind: ORDER_KIND,
        content: ciphertext,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      // Publish to the fixed order relays so the operator can always find it,
      // and to the default pool for extra reach.
      await Promise.allSettled([
        nostr.group(ORDER_RELAYS).event(event, {
          signal: AbortSignal.timeout(5000),
        }),
        nostr.event(event, { signal: AbortSignal.timeout(5000) }),
      ]);
      return event;
    },
  });
}
